// npm
import React, { useEffect, useState } from 'react'
import { alpha, styled } from '@mui/material'
import LightboxBase, { SlideImage } from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import { TfiZoomIn, TfiZoomOut, TfiClose } from 'react-icons/tfi'

// components
import NavButton from '@atoms/buttons/NavButton'

// images
import ChevronRight from '@images/icons/chevron-right.svg'

interface Props {
  open: boolean
  index: number
  slides: SlideImage[]
  resetParentState: Function
}

const StyledLightbox = styled(LightboxBase)(({ theme }) => ({
  ['.yarl__container']: {
    backgroundColor: theme.palette.secondary.main,
  },
  ['.yarl__toolbar']: {
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    borderWidth: '0 1px 1px',
    padding: 0,
    right: '20px',
    [theme.breakpoints.down('md')]: {
      right: 'auto',
      left: '50%',
      width: '180px',
      transform: 'translateX(-50%)',
    },
    ['.yarl__button']: {
      width: '60px',
      height: '60px',
      backgroundColor: alpha(theme.palette.secondary.main, 1),
      filter: 'none',
      transition: theme.transitions.create(['background-color', 'color']),
      ['&:disabled']: {
        color: alpha(theme.palette.common.white, 0.5),
      },
      ['&:not(:last-of-type)']: {
        borderRight: `1px solid ${theme.palette.primary.main}`,
      },
    },
  },
}))

const StyledNavButton = styled(NavButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [theme.breakpoints.down('md')]: {
    top: 'auto',
    bottom: 0,
    transform: 'none',
  },
}))

const Lightbox = ({ slides, open, index, resetParentState }: Props) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const ref = React.useRef(null)

  const handleClose = () => {
    resetParentState()
    setLightboxOpen(false)
  }

  useEffect(() => {
    setLightboxOpen(open)
    setLightboxIndex(index)
  }, [open, index])

  return (
    <StyledLightbox
      controller={{ ref }}
      open={lightboxOpen}
      index={lightboxIndex}
      plugins={[Zoom]}
      close={handleClose}
      slides={slides}
      render={{
        buttonPrev:
          slides.length <= 1
            ? () => null
            : () => {
                return (
                  <StyledNavButton
                    onClick={() => ref.current?.prev()}
                    themeVariant="dark"
                    aria-label="previous"
                    sx={{
                      svg: {
                        transform: 'rotate(180deg)',
                      },
                      left: {
                        xs: '50%',
                        md: 20,
                      },
                      transform: {
                        xs: 'translateX(-100%)',
                        md: 'none',
                      },
                    }}
                  >
                    <ChevronRight />
                  </StyledNavButton>
                )
              },
        buttonNext:
          slides.length <= 1
            ? () => null
            : () => {
                return (
                  <StyledNavButton
                    onClick={() => ref.current?.next()}
                    themeVariant="dark"
                    aria-label="next"
                    sx={{
                      right: {
                        xs: '50%',
                        md: 20,
                      },
                      transform: {
                        xs: 'translateX(calc(100% - 1px))',
                        md: 'none',
                      },
                    }}
                  >
                    <ChevronRight />
                  </StyledNavButton>
                )
              },
        iconZoomIn: () => <TfiZoomIn fontSize="2.2rem" />,
        iconZoomOut: () => <TfiZoomOut fontSize="2.2rem" />,
        iconClose: () => <TfiClose fontSize="1.8rem" />,
      }}
      zoom={{
        zoomInMultiplier: 5,
      }}
      carousel={{ preload: 1, padding: 0, finite: slides.length <= 1 }}
    />
  )
}

export default Lightbox
